import { createRouter } from "@tanstack/react-router";
import { RootLoader } from "./components/custom/root-loader";
import { CustomErrorComponent } from "./components/custom/router/custom-error";
import { NotFoundRoute } from "./components/custom/router/not-found-route";
import { queryClient } from "./lib/query-client";
import { routeTree } from "./routeTree.gen";

// Constants for timing configurations
const MILLISECONDS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const DEFAULT_STALE_TIME_SECONDS = 3;
const PRELOAD_GC_TIME_HOURS = 1;

// Create a new router instance
export const router = createRouter({
  routeTree,
  basepath: "/",
  defaultNotFoundComponent: NotFoundRoute,
  defaultErrorComponent: CustomErrorComponent,
  // Show the pending UI immediately instead of after the 1000ms default. On a
  // hard load there is no previous screen to hold, so during the pending window
  // the router renders nothing — a blank dark screen. Context-resolving redirect
  // routes that have no component of their own (`/`, `/orgs/$id`,
  // `/orgs/$id/projects/$id/`) spend that whole window in their async beforeLoad
  // (org + project + eligibility fetches, then a redirect), so the default debounce
  // means the screen stays black until everything resolves. 0 surfaces the loader
  // at once. Client navigations keep their previous screen via startTransition, so
  // this does not add a loader flash to in-app navigation.
  defaultPendingMs: 0,
  // Fallback loader for routes that have no pendingComponent of their own and no
  // pending ancestor (e.g. top-level routes outside `_authenticated`).
  defaultPendingComponent: RootLoader,
  defaultPreload: "intent",
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
  defaultStaleTime: MILLISECONDS_PER_SECOND * DEFAULT_STALE_TIME_SECONDS,
  defaultPreloadGcTime:
    MILLISECONDS_PER_SECOND *
    SECONDS_PER_MINUTE *
    MINUTES_PER_HOUR *
    PRELOAD_GC_TIME_HOURS,
  context: {
    queryClient,
  },
});

type Router = typeof router;

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  // biome-ignore lint/style/useConsistentTypeDefinitions: This is ok
  interface Register {
    router: Router;
  }
}
